import * as Immutable from 'immutable';
import moment from 'moment';
import * as React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import analytics from '../../shared/services/analytics/analytics';
import { localeData } from '../../locales/data';
import { FormattedMessageHelper } from '../../shared/helpers/internationalization';
import { updateAlphaProperties } from '../../actions/AlphasActions';
import {
  clearAlphaProperties,
  clearScrollToAlphaProperties,
  hasUpdatedProperties,
  resetProperties,
  setAlphaPropertiesData,
  setFieldOnFocus,
  setTagsOptions,
} from '../../actions/AlphasPropertiesAction';
import { saveSimulationProperties, updateAlphaPropertiesSimulation } from '../../actions/Simulation/DataAction';
import { StateType } from '../../reducers';
import {
  alphasFetchingPropertiesSelector,
  alphasOptionsOsmosisPointsSelector,
  alphasOptionsSelector,
  getIsAlphaSubmittedSelector,
} from '../../reducers/AlphasReducer/AlphasSelector';
import {
  alphaPropertiesCategorySelector,
  alphaPropertiesColorSelector,
  alphaPropertiesComboDescriptionSelector,
  alphaPropertiesErrorCategorySelector,
  alphaPropertiesErrorColorSelector,
  alphaPropertiesErrorComboSelector,
  alphaPropertiesErrorNameSelector,
  alphaPropertiesErrorOsmosisPointsSelector,
  alphaPropertiesErrorRegularSelector,
  alphaPropertiesErrorSelectionSelector,
  alphaPropertiesErrorTagsSelector,
  alphaPropertiesFieldOnFocusSelector,
  alphaPropertiesHasErrorsSelector,
  alphaPropertiesNameSelector,
  alphaPropertiesOsmosisPointsSelector,
  alphaPropertiesRegularDescriptionSelector,
  alphaPropertiesSelectionDescriptionSelector,
  alphaPropertiesShowErrorsSelector,
  alphaPropertiesTagsOptionsSelector,
  alphaPropertiesTagsSelector,
  getHasUpdatedPropertiesSelector,
} from '../../reducers/AlphaPropertiesReducer/AlphaPropertiesSelector';
import { activeTabIndexSelector, activeTabSelector } from '../../reducers/EditorReducer/EditorSelector';
import { isAlphaCreatedByTheCurrentUserSelector } from '../../reducers/GenericSelectors';
import { dataSelector } from '../../reducers/SimulationReducer/DataReducer/DataSelector';
import { convertArrayFromDropdownProperties } from '../../shared/helpers';
import { getAlphasRequiredProperties } from '../../shared/helpers/AlphaPropertiesValidation';
import { DispatchConnect } from '../../types';

import {
  AlphasPropertiesDispatchProps,
  AlphasPropertiesGenericProps,
  AlphasPropertiesProps,
  AlphasPropertiesStateProps,
  OptionsProps,
} from './types';
import { AlphaPropertiesMain } from './AlphaPropertiesMain';

class AlphaProperties extends React.Component<AlphasPropertiesGenericProps> {
  private timeOut: any;
  private timeOutShouldScroll: any;

  componentDidMount() {
    clearTimeout(this.timeOutShouldScroll);
    this.timeOutShouldScroll = setTimeout(() => this.props.dispatchClearScrollToAlphaProperties(), 3000);
    if (this.props.data && !this.props.dontOverrideProperties) {
      this.setData(this.props);
    } else {
      this.setTemporaryData(this.props);
    }

    if (this.props.alphasOptions) {
      if (this.props.alphasOptions.getIn(['tags', 'choices']) && this.props.tagsOptions.size === 0) {
        this.props.dispatchSetTagsOptions(
          // @ts-expect-error ImmutableJS getIn returns unknown; toJS() loses typing
          convertArrayFromDropdownProperties(this.props.alphasOptions.getIn(['tags', 'choices']).toJS())
        );
      }
    }
  }

  componentWillUnmount() {
    clearTimeout(this.timeOut);
    clearTimeout(this.timeOutShouldScroll);
  }

  setTemporaryData = (props: AlphasPropertiesGenericProps) => {
    const fields = ['color', 'name', 'tags', 'category', 'osmosisPoints', 'regular', 'combo', 'selection'];
    let data = {};
    fields.forEach((field: any) => {
      if (field === 'tags') {
        let tags = props.temporaryData && props.temporaryData.get('tags');
        tags = tags && tags.size !== 0 ? tags : Immutable.List();
        data = { ...data, ...{ tags } };
      } else if (field === 'combo' || field === 'selection' || field === 'regular') {
        const value =
          props.temporaryData && props.temporaryData.getIn([field, 'description']) ? props.temporaryData.getIn([field, 'description']) : '';
        data = { ...data, ...{ [field]: { description: value, error: Immutable.List() } } };
      } else if (field === 'osmosisPoints') {
        const value =
          props.temporaryData && props.temporaryData.get(field) !== null && props.temporaryData.get(field) !== undefined
            ? props.temporaryData.get(field)
            : undefined;
        data = { ...data, ...{ [field]: value } };
      } else {
        const value = props.temporaryData && props.temporaryData.get(field) !== null ? props.temporaryData.get(field) : '';
        data = { ...data, ...{ [field]: value } };
      }
    });
    if (Object.keys(data).length !== 0) {
      this.props.dispatchSetAlphaPropertiesData(data);
    }
  };

  setData(props: AlphasPropertiesGenericProps) {
    this.setTemporaryData(props);
    const fields = ['color', 'name', 'tags', 'category', 'osmosisPoints', 'regular', 'combo', 'selection'];
    let data = {};
    fields.forEach((field: any) => {
      if (field === 'tags') {
        let tags = props.data && props.data.get('tags');
        tags = tags && tags.size !== 0 ? tags : Immutable.List();
        data = { ...data, ...{ tags } };
      } else if (field === 'combo' || field === 'selection' || field === 'regular') {
        const value = props.data && props.data.getIn([field, 'description']) ? props.data.getIn([field, 'description']) : '';
        data = { ...data, ...{ [field]: { description: value, error: Immutable.List() } } };
      } else if (field === 'osmosisPoints') {
        const value =
          props.data && props.data.get(field) !== null && props.data.get(field) !== undefined ? props.data.get(field) : undefined;
        data = { ...data, ...{ [field]: value } };
      } else {
        const value = props.data && props.data.get(field) !== null ? props.data.get(field) : '';
        data = { ...data, ...{ [field]: value } };
      }
      if (Object.keys(data).length !== 0 && props.data) {
        this.props.dispatchSetAlphaPropertiesData({ ...data, showErrors: false });
      }
    });
  }

  componentDidUpdate(prevProps: AlphasPropertiesGenericProps) {
    const childId = this.props.activeTab && this.props.activeTab.getIn(['childTabs', this.props.indexChild, 'id']);
    const prevChildId = prevProps.activeTab && prevProps.activeTab.getIn(['childTabs', prevProps.indexChild, 'id']);

    if (this.props.activeTabIndex !== prevProps.activeTabIndex || childId !== prevChildId || (!prevProps.data && this.props.data)) {
      this.setData(this.props);
      clearTimeout(this.timeOut);
      this.timeOut = null;
    }

    if (prevProps.data && this.props.data && prevProps.data.get('id') !== this.props.data.get('id')) {
      prevProps.dispatchResetProperties();
      this.setData(this.props);
    }

    if (this.props.alphasOptions && prevProps.alphasOptions !== this.props.alphasOptions && this.props.tagsOptions.size === 0) {
      prevProps.dispatchSetTagsOptions(
        // @ts-expect-error ImmutableJS getIn returns unknown; toJS() loses typing
        convertArrayFromDropdownProperties(this.props.alphasOptions.getIn(['tags', 'choices']).toJS())
      );
    }
  }

  handleChangeDescriptions = (_: any, data: any) => {
    if (data.name === 'regular') {
      analytics.trackCustomEventOnce('sim_edit_property', { event_label: 'description' });
    }
    this.props.dispatchHasUpdatedProperties();
    const arrayOfValidations = getAlphasRequiredProperties(this.props.data).get(data.name);
    let errors = Immutable.List();
    if (this.props.showErrors && arrayOfValidations && arrayOfValidations.size > 0) {
      errors = arrayOfValidations.reduce((totalErrors: any, validation: any) => {
        const validationErrors = validation(data.value);

        return totalErrors.concat(validationErrors);
      }, Immutable.List());
    }
    this.props.dispatchSetAlphaPropertiesData({
      [data.name]: { description: data.value, error: errors },
      shouldScrollToAlphaProperties: true,
      fieldOnFocus: data.name,
    });
    if (this.props.isSimulate) {
      const childId = this.props.isMultiTab ? this.props.activeTab.getIn(['childTabs', this.props.indexChild, 'id']) : null;
      this.props.dispatchSaveSimulationProperties(this.props.activeTab.get('id'), childId, {
        [data.name]: { description: data.value, error: errors },
      });
      clearTimeout(this.timeOut);
      this.timeOut = setTimeout(() => this.handlePropertiesSave(), 1000);
      clearTimeout(this.timeOutShouldScroll);
      this.timeOutShouldScroll = setTimeout(() => this.props.dispatchClearScrollToAlphaProperties(), 3000);
    } else {
      clearTimeout(this.timeOut);
      this.timeOut = setTimeout(() => this.handlePropertiesSave(), 1000);
    }
  };

  handleChange = (_: any, data: any) => {
    const fieldLabelMap: Record<string, string> = {
      name: 'name',
      category: 'category',
      tags: 'tags',
    };
    if (fieldLabelMap[data.name]) {
      analytics.trackCustomEventOnce('sim_edit_property', { event_label: fieldLabelMap[data.name] });
    }
    this.props.dispatchHasUpdatedProperties();

    // Handle osmosisPoints validation
    let value = data.value;
    let errors = Immutable.List();

    if (data.name === 'osmosisPoints') {
      // Convert empty string to undefined for osmosisPoints
      if (value === '' || value === null) {
        value = undefined;
      } else {
        const numValue = Number(value);
        if (this.props.showErrors && (isNaN(numValue) || numValue < 1 || numValue > 100000)) {
          errors = Immutable.List(['edit.properties.osmosisPoints.range']);
        }
        value = numValue;
      }
    }

    this.props.dispatchSetAlphaPropertiesData({
      [data.name]: value,
      [`error${data.name}`]: errors,
      shouldScrollToAlphaProperties: true,
      fieldOnFocus: data.name,
    });

    if (this.props.isSimulate) {
      const childId = this.props.isMultiTab ? this.props.activeTab.getIn(['childTabs', this.props.indexChild, 'id']) : null;
      this.props.dispatchSaveSimulationProperties(this.props.activeTab.get('id'), childId, { [data.name]: value });
      clearTimeout(this.timeOut);
      //todo: move this logic to actions or redux
      this.timeOut = setTimeout(() => this.handlePropertiesSave(), 1000);
      clearTimeout(this.timeOutShouldScroll);
      this.timeOutShouldScroll = setTimeout(() => this.props.dispatchClearScrollToAlphaProperties(), 3000);
    } else {
      clearTimeout(this.timeOut);
      //todo: move this logic to actions or redux
      this.timeOut = setTimeout(() => this.handlePropertiesSave(), 1000);
    }
  };

  handleAddition = (_: any, data: any) => {
    const value = data.value;
    const newdata = this.props.tagsOptions.concat({ key: value, text: value, value });
    // @ts-expect-error ImmutableJS toJS() loses typing
    this.props.dispatchSetTagsOptions(newdata.toJS());
  };

  isSuperAlphaProperty = () =>
    (this.props.isSimulate && this.props.superAlpha) || (this.props.data && this.props.data.get('type') === 'SUPER');

  validateSuperAlphaDescriptionProperties = () => {
    const currentData = this.props.data;
    const isSuperAlpha = this.isSuperAlphaProperty();

    if (!isSuperAlpha) {
      return { hasErrors: false };
    }

    const previousCombo = currentData && currentData.getIn(['combo', 'description']);
    const previousSelection = currentData && currentData.getIn(['selection', 'description']);
    const clearedFields: string[] = [];
    if (this.props.combo === '' && previousCombo) clearedFields.push('combo');
    if (this.props.selection === '' && previousSelection) clearedFields.push('selection');

    if (clearedFields.length > 0) {
      const errorPayload: Record<string, any> = {};
      clearedFields.forEach(field => {
        errorPayload[field] = { description: '', error: Immutable.fromJS(['field.required.100.characters']) };
      });
      this.props.dispatchSetAlphaPropertiesData(errorPayload);
      return { hasErrors: true };
    }
    return { hasErrors: false };
  };

  handlePropertiesSave = () => {
    clearTimeout(this.timeOut);
    const { hasErrors } = this.validateSuperAlphaDescriptionProperties();
    if (this.props.propertiesHaveErrors || hasErrors) {
      return;
    }
    const currentData = this.props.data;
    this.props.dispatchResetProperties();
    const isSuperAlpha = this.isSuperAlphaProperty();
    let payload: any = {
      color: this.props.color !== 'NONE' && this.props.color.trim().length !== 0 ? this.props.color : null,
      name: this.props.name !== '' ? this.props.name : null,
      tags: this.props.tags.toJS(),
      category: this.props.category !== '' ? this.props.category : null,
      regular: !isSuperAlpha && this.props.regular !== '' ? { description: this.props.regular } : { description: null },
    };

    // Add osmosisPoints only if the options are available AND the alpha is submitted
    if (this.props.osmosisOptions && this.props.isSubmitted) {
      payload.osmosisPoints = this.props.osmosisPoints !== undefined ? this.props.osmosisPoints : null;
    }

    if (isSuperAlpha) {
      payload = {
        ...payload,
        ...(this.props.combo !== '' ? { combo: { description: this.props.combo } } : {}),
        ...(this.props.selection !== '' ? { selection: { description: this.props.selection } } : {}),
      };
    }
    const currentDataPayload: any = {};
    Object.keys(payload).map((key: string) => {
      currentDataPayload[key] = currentData && currentData.get(key);
    });

    if (Immutable.fromJS(payload).equals(Immutable.fromJS(currentDataPayload))) {
      return;
    }

    const alphaId = currentData && currentData.get('id');
    if (this.props.isSimulate && !currentData) {
      const childId = this.props.isMultiTab ? this.props.activeTab.getIn(['childTabs', this.props.indexChild, 'id']) : null;
      this.props.dispatchSaveSimulationProperties(this.props.activeTab.get('id'), childId, payload);
    } else if (this.props.isSimulate === true && this.props.tabIndex !== undefined) {
      const childId = this.props.isMultiTab ? this.props.activeTab.getIn(['childTabs', this.props.indexChild, 'id']) : null;
      this.props.dispatchSaveSimulationProperties(this.props.activeTab.get('id'), childId, payload);
      this.props.dispatchSaveAlphaProperties(alphaId, payload, this.props.activeTab.get('id'), childId);
    } else {
      this.props.dispatchUpdateAlphaProperties(alphaId, payload);
    }
  };

  handleColorChange = (color: string) => {
    analytics.trackCustomEventOnce('sim_edit_property', { event_label: 'color' });
    this.props.dispatchHasUpdatedProperties();
    this.props.dispatchSetAlphaPropertiesData({ color: color, shouldScrollToAlphaProperties: true, fieldOnFocus: 'color' });
    if (this.props.isSimulate) {
      const childId = this.props.isMultiTab ? this.props.activeTab.getIn(['childTabs', this.props.indexChild, 'id']) : null;
      this.props.dispatchSaveSimulationProperties(this.props.activeTab.get('id'), childId, { color });
      clearTimeout(this.timeOut);
      //todo: move this logic to actions or redux
      this.timeOut = setTimeout(() => this.handlePropertiesSave(), 1000);
      clearTimeout(this.timeOutShouldScroll);
      this.timeOutShouldScroll = setTimeout(() => this.props.dispatchClearScrollToAlphaProperties(), 3000);
    } else {
      clearTimeout(this.timeOut);
      //todo: move this logic to actions or redux
      this.timeOut = setTimeout(() => this.handlePropertiesSave(), 1000);
    }
  };

  handleFocus = (event: any, data: any) => {
    if (this.props.isSimulate) {
      const field = data ? data.name : event.target.name;
      const elem: any = document.querySelector(`[name=${field}]`);
      if (elem && elem.value) {
        const size = elem.value.length;
        elem.focus();
        if (elem.type !== 'number') {
          elem.setSelectionRange(size, size);
        }
      }

      this.props.dispatchSetFieldOnFocus(field);
    }
  };

  handleOnBlur = () => {
    //todo: rewrite so that this logic is moved to redux
    this.timeOut = setTimeout(() => this.handlePropertiesSave(), 1000);
    if (this.props.isSimulate) {
      this.props.dispatchSetFieldOnFocus(null);
    }
  };

  render() {
    const {
      color,
      name,
      tags,
      category,
      regular,
      selection,
      tagsOptions,
      alphasOptions,
      isSimulate,
      superAlpha,
      combo,
      tabIndex,
      indexChild,
      isMultiTab,
      fetchingProperties,
      dataReducer,
      id = 'alpha-properties',
      isAlphaCreatedByTheCurrentUser,
    } = this.props;
    let options = [{ label: 'None', value: '' }];
    if (alphasOptions && alphasOptions.getIn(['category', 'choices'])) {
      // @ts-expect-error ImmutableJS getIn returns unknown; toJS() loses typing
      options = options.concat(alphasOptions.getIn(['category', 'choices']).toJS());
    }

    let fetchingEditProperties = fetchingProperties;
    const lastSavedDate = this.props.data && this.props.data.get('dateModified');

    if (isSimulate === true) {
      if (isMultiTab === true) {
        // @ts-expect-error dataReducer.getIn returns unknown; target expects boolean
        fetchingEditProperties = dataReducer.getIn([tabIndex, 'children', indexChild, 'fetchingAlphaPropertiesPatch']);
      } else {
        // @ts-expect-error dataReducer.getIn returns unknown; target expects boolean
        fetchingEditProperties = dataReducer.getIn([tabIndex, 'fetchingAlphaPropertiesPatch']);
      }
    }

    const ORIGIN_DISPLAY_NAMES: Record<string, string> = {
      BRAINLABS: 'BRAIN Labs',
      PLATFORM: 'BRAIN Platform',
    };
    const originRaw: string = this.props.data?.get('origin') ?? '';
    const originDisplay = ORIGIN_DISPLAY_NAMES[originRaw] ?? originRaw;

    const effectiveIsOwner = isAlphaCreatedByTheCurrentUser || !!isSimulate;

    const message = effectiveIsOwner
      ? { id: 'alpha.properties.cannot.view.withOrigin', values: { origin: originDisplay } }
      : { id: 'alpha.properties.cannot.view.noUser', values: {} };

    const shouldHideProperties = !isSimulate && (!isAlphaCreatedByTheCurrentUser || originRaw === 'BRAINLABS');

    if (shouldHideProperties) {
      return (
        <div id={id} className="alphas-details-content__box">
          <div className="alphas_properties">
            <header className="alphas_properties__header">
              <FormattedMessageHelper tagName="span" id="edit.properties.title" defaultMessage={localeData.en['edit.properties.title']} />
            </header>
            <div className="alpha-properties__message-restricted">
              <FormattedMessageHelper id={message.id} defaultMessage={localeData.en[message.id]} values={message.values} />
            </div>
          </div>
        </div>
      );
    }

    return (
      <div id={id} className="alphas-details-content__box">
        <AlphaPropertiesMain
          color={color}
          handleFocus={this.handleFocus}
          name={name}
          // @ts-expect-error ImmutableJS toJS() loses typing
          tags={tags && tags.size === 0 ? [] : tags.toJS()}
          category={category}
          regular={regular}
          handleChange={this.handleChange}
          handleColorChange={this.handleColorChange}
          // @ts-expect-error ImmutableJS toJS() loses typing
          tagsOptions={tagsOptions && tagsOptions.toJS()}
          handleAddition={this.handleAddition}
          options={options}
          fetchingProperties={fetchingEditProperties}
          isSimulate={isSimulate}
          superAlpha={superAlpha || (this.props.data && this.props.data.get('type') === 'SUPER')}
          handleChangeDescriptions={this.handleChangeDescriptions}
          selection={selection}
          handleOnBlur={this.handleOnBlur}
          combo={combo}
          hasAlpha={Boolean(this.props.data)}
          alphaDetail={this.props.data}
          lastSavedDate={lastSavedDate ? moment(lastSavedDate).format('ddd, L, LT') : null}
          errorcolor={this.props.errorcolor}
          errorname={this.props.errorname}
          errortags={this.props.errortags}
          errorcategory={this.props.errorcategory}
          errorosmosisPoints={this.props.errorosmosisPoints}
          errorregular={this.props.errorregular}
          errorselection={this.props.errorselection}
          errorcombo={this.props.errorcombo}
          fieldOnFocus={this.props.fieldOnFocus}
          haveResults={this.props.haveResults}
          isAlphaCreatedByTheCurrentUser={isAlphaCreatedByTheCurrentUser || isSimulate || false}
          osmosisPointsOptions={this.props.osmosisOptions}
          osmosisPoints={this.props.osmosisPoints}
          isSubmitted={this.props.isSubmitted}
          onCopyAlphaId={() =>
            isSimulate
              ? analytics.trackCustomEvent('sim_click_copy_alpha_id')
              : analytics.trackCustomEvent('alphas_click_copy_id', { event_label: 'properties' })
          }
        />
      </div>
    );
  }
}

const mapStateToProps = (state: StateType, props: AlphasPropertiesProps): AlphasPropertiesStateProps => {
  const dataPath = props.path.concat(['alphaDetail']);

  return {
    // @ts-expect-error connect own-props collapse under @types/react@19
    color: alphaPropertiesColorSelector(state),
    // @ts-expect-error connect own-props collapse under @types/react@19
    name: alphaPropertiesNameSelector(state),
    // @ts-expect-error connect own-props collapse under @types/react@19
    tags: alphaPropertiesTagsSelector(state),
    // @ts-expect-error connect own-props collapse under @types/react@19
    category: alphaPropertiesCategorySelector(state),
    // @ts-expect-error connect own-props collapse under @types/react@19
    regular: alphaPropertiesRegularDescriptionSelector(state),
    // @ts-expect-error connect own-props collapse under @types/react@19
    selection: alphaPropertiesSelectionDescriptionSelector(state),
    // @ts-expect-error connect own-props collapse under @types/react@19
    combo: alphaPropertiesComboDescriptionSelector(state),
    // @ts-expect-error connect own-props collapse under @types/react@19
    errorcolor: alphaPropertiesErrorColorSelector(state),
    // @ts-expect-error connect own-props collapse under @types/react@19
    errorname: alphaPropertiesErrorNameSelector(state),
    // @ts-expect-error connect own-props collapse under @types/react@19
    errortags: alphaPropertiesErrorTagsSelector(state),
    // @ts-expect-error connect own-props collapse under @types/react@19
    errorcategory: alphaPropertiesErrorCategorySelector(state),
    // @ts-expect-error connect own-props collapse under @types/react@19
    errorosmosisPoints: alphaPropertiesErrorOsmosisPointsSelector(state),
    // @ts-expect-error connect own-props collapse under @types/react@19
    errorregular: alphaPropertiesErrorRegularSelector(state),
    // @ts-expect-error connect own-props collapse under @types/react@19
    errorselection: alphaPropertiesErrorSelectionSelector(state),
    // @ts-expect-error connect own-props collapse under @types/react@19
    errorcombo: alphaPropertiesErrorComboSelector(state),
    // @ts-expect-error connect own-props collapse under @types/react@19
    alphasOptions: alphasOptionsSelector(state),
    // @ts-expect-error connect own-props collapse under @types/react@19
    tagsOptions: alphaPropertiesTagsOptionsSelector(state),
    // @ts-expect-error connect own-props collapse under @types/react@19
    fieldOnFocus: alphaPropertiesFieldOnFocusSelector(state),
    // @ts-expect-error connect own-props collapse under @types/react@19
    fetchingProperties: alphasFetchingPropertiesSelector(state),
    // @ts-expect-error connect own-props collapse under @types/react@19
    dataReducer: dataSelector(state),
    // @ts-expect-error connect own-props collapse under @types/react@19
    hasUpdatedProperties: getHasUpdatedPropertiesSelector(state),
    activeTab: activeTabSelector(state),
    activeTabIndex: activeTabIndexSelector(state),
    data: state.getIn(dataPath),
    temporaryData: state.getIn(props.path.concat(['alphaProperties'])),
    // @ts-expect-error connect own-props collapse under @types/react@19
    hasUpdatedPropertiesTab: state.getIn(props.path.concat(['alphaProperties', 'hasUpdatedProperties'])),
    // @ts-expect-error connect own-props collapse under @types/react@19
    superAlpha: state.getIn(props.path.concat(['superAlpha'])),
    // @ts-expect-error connect own-props collapse under @types/react@19
    dontOverrideProperties: state.getIn(props.path.concat(['alphaProperties', 'dontOverrideProperties'])),
    // @ts-expect-error connect own-props collapse under @types/react@19
    showErrors: alphaPropertiesShowErrorsSelector(state),
    propertiesHaveErrors: alphaPropertiesHasErrorsSelector(state),
    isAlphaCreatedByTheCurrentUser: isAlphaCreatedByTheCurrentUserSelector(state),
    osmosisOptions: alphasOptionsOsmosisPointsSelector(state),
    osmosisPoints: alphaPropertiesOsmosisPointsSelector(state),
    isSubmitted: getIsAlphaSubmittedSelector(state),
  };
};

const mapDispatchToProps = (dispatch: Dispatch<any>): AlphasPropertiesDispatchProps => ({
  dispatchSaveSimulationProperties: (idParent: string, idChild: string | null, data: any) =>
    dispatch(saveSimulationProperties(idParent, idChild, data)),
  dispatchSetAlphaPropertiesData: (data: any) => dispatch(setAlphaPropertiesData(data)),
  dispatchSetTagsOptions: (data: OptionsProps[]) => dispatch(setTagsOptions(data)),
  dispatchClearAlphaProperties: () => dispatch(clearAlphaProperties()),
  dispatchHasUpdatedProperties: () => dispatch(hasUpdatedProperties()),
  dispatchResetProperties: () => dispatch(resetProperties()),
  dispatchSetFieldOnFocus: (name: string | null) => dispatch(setFieldOnFocus(name)),
  dispatchClearScrollToAlphaProperties: () => dispatch(clearScrollToAlphaProperties()),
  dispatchUpdateAlphaProperties: async (alphaId: string, data: any) => dispatch(updateAlphaProperties(alphaId, data)),
  dispatchSaveAlphaProperties: async (alphaId: string, data: any, idParent: string, idChild: string | null) =>
    dispatch(updateAlphaPropertiesSimulation(alphaId, data, idParent, idChild)),
});

// `forwardRef: true` lets AlphaProperties.test.tsx reach the instance via a ref (RTL replacement for
// enzyme's `.instance()`); kept because handlers go through semantic-ui Dropdowns/debounce, not clean DOM.
export default connect<AlphasPropertiesStateProps, DispatchConnect<AlphasPropertiesDispatchProps>, any, StateType>(
  mapStateToProps,
  mapDispatchToProps,
  undefined,
  { forwardRef: true }
)(AlphaProperties);
